<template>
  <ContactReviewDisplayComponent
    :accountData="accountData"
    :accountRules="accountRules"
    :companyRules="companyRules"
    :companyIndex="companyIndex"
    :countryCode="'LT'"
    :shouldShowCompanyFields="shouldShowCompanyFields"
    @updateAccountData="$emit('updateAccountData', $event)"
    @handleUpdateCompany="$emit('handleUpdateCompany', $event)"
    @selectExistingCompany="$emit('selectExistingCompany', $event)"
    @resetCompanySelection="$emit('resetCompanySelection')"
  >
    <template #account="{ phoneCountryCode, accountField }">
      <TextRowComponent v-bind="accountField('name')" />
      <TextRowComponent
        v-if="!shouldShowCompanyFields"
        v-bind="accountField('registry_code')"
      />
      <TextRowComponent
        v-bind="accountField('phone_number')"
        type="tel"
        :countryCode="phoneCountryCode"
      />
    </template>
    <template #company="{ locked, companyField }">
      <template v-if="shouldShowCompanyFields">
        <TextRowComponent v-bind="companyField('name')" :disabled="locked" />
        <TextRowComponent
          v-bind="companyField('registry_code')"
          :disabled="locked"
        />
      </template>
      <TextRowComponent v-bind="companyField('address')" :disabled="locked" />
    </template>
  </ContactReviewDisplayComponent>
</template>

<script setup lang="ts">
import { watch } from "vue";
import type { Account, Company } from "@/types";
import { useValidators } from "@/composables/useValidators";
import { useI18n } from "vue-i18n";
import type { AccountRules, CompanyRules } from "@/signup/common/contactFields";
import TextRowComponent from "@/signup/common/TextRowComponent.vue";
import ContactReviewDisplayComponent from "@/signup/ContactReviewDisplayComponent.vue";

const props = withDefaults(
  defineProps<{
    accountData: Account;
    companyIndex: number;
    shouldShowCompanyFields?: boolean;
  }>(),
  { shouldShowCompanyFields: true }
);

const emit = defineEmits<{
  updateAccountData: [accountData: Account];
  handleUpdateCompany: [company: Company];
  selectExistingCompany: [companyId: number];
  resetCompanySelection: [];
}>();

const { t } = useI18n();
const {
  required,
  lithuanianCompanyNumber,
  lithuanianPersonalID,
  personalName,
  phoneNumber,
} = useValidators(t);

// A row checks its rules only while shown: a private individual, their own company, gives a personal
// ID, and the company only its address.
const accountRules: AccountRules = {
  name: { required, personalName },
  registry_code: { required, lithuanianPersonalID },
  phone_number: { required, phoneNumber },
};
const companyRules: CompanyRules = {
  name: { required },
  registry_code: { required, lithuanianCompanyNumber },
  address: { required },
};

// A private individual is their own company, so the company's name and registry code
// track the account holder's. The mode is watched alongside the value, and re-checked on
// every run, because the LT flow flips it on a live instance: the user type lives in a
// query param and LTSignupFlow's `:key="companyIndex"` does not change with it. Reading
// the mode only at setup stranded the mirroring in whichever direction the component
// happened to mount in.
watch(
  [() => props.shouldShowCompanyFields, () => props.accountData.name],
  ([showsCompanyFields, newName]) => {
    if (!showsCompanyFields && newName && props.companyIndex !== -1) {
      emit("handleUpdateCompany", {
        ...props.accountData.companies[props.companyIndex],
        name: newName,
      });
    }
  },
  { immediate: true }
);

watch(
  [() => props.shouldShowCompanyFields, () => props.accountData.registry_code],
  ([showsCompanyFields, newCode]) => {
    if (!showsCompanyFields && newCode && props.companyIndex !== -1) {
      emit("handleUpdateCompany", {
        ...props.accountData.companies[props.companyIndex],
        registry_code: newCode,
      });
    }
  },
  { immediate: true }
);
</script>
